import type { AdminUserListItem, AuthUser } from '@proref/types';
import type { UpdateUserInput, UpdateMeInput } from '@proref/schemas';
import { api, unwrap, type Envelope } from '../lib/api';

// The roster visible to the caller: every platform user for super_admin (the platform
// dashboard), or just the caller's own federation for federation_admin (the Invite-users
// page's Users tab). The API resolves which from the JWT — never a client-supplied filter.
// Both embed each user's federation + sport display names (both null for a super_admin).
export async function listUsers(): Promise<AdminUserListItem[]> {
  const res = await api.get<Envelope<AdminUserListItem[]>>('/users');
  return unwrap(res.data);
}

// Remove a user (referee or admin) from the roster. federation_admin is scoped server-side to
// their own federation; neither role can remove themselves. Cascades their data and revokes login.
export async function removeUser(id: string): Promise<void> {
  await api.delete<Envelope<null>>(`/users/${id}`);
}

// Edit a roster member. Send ONLY the fields that actually changed: the API refuses a
// federation_admin that sends federation_id/sport_id at all, and refuses any role/tenancy key on
// a super_admin target — so echoing back unchanged values would turn a rename into a 403.
// Returns the updated row in the list's own shape, ready to drop into the table.
export async function updateUser(id: string, input: UpdateUserInput): Promise<AdminUserListItem> {
  const res = await api.patch<Envelope<AdminUserListItem>>(`/users/${id}`, input);
  return unwrap(res.data);
}

// Edit your OWN profile. Deliberately a different endpoint from updateUser above: that one is
// admin-guarded and takes a target id, this one takes its target from the JWT — so a self-edit can
// never be one missing scope check away from being an admin edit. Returns the full /me shape,
// ready to drop straight into the auth context.
export async function updateMe(input: UpdateMeInput): Promise<AuthUser> {
  const res = await api.patch<Envelope<AuthUser>>('/me', input);
  return unwrap(res.data);
}
